import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { Stethoscope, Building2, Search, Microscope, PenLine, Clock, Moon, Hourglass } from "lucide-react";
import { Reveal, Honeypot, inputClass, scrollToId } from "@/components/Shared";
import { PRACTITIONERS, useConcierge } from "@/components/Concierge";
import { postEnquiry } from "@/lib/api";
import { trackEvent, leadContext } from "@/lib/analytics";
import { getHairStage } from "@/lib/hairStage";
import { HairStageChip } from "@/components/HairStageChip";
import { CLINIC, RESPONSE_PROMISE, getTeamMember } from "@/data/content";
import { PROOF_SECOND } from "@/data/landing";

const SECOND_ICON = { skin: Search, hair: Microscope, procedure: PenLine };

export const ProofStrip = ({ variant = "procedure", className = "" }) => {
  const Second = SECOND_ICON[variant] || PenLine;
  const items = [
    { Icon: Stethoscope, label: "Doctor-run", sub: "A physician assesses you first" },
    { Icon: Second, label: PROOF_SECOND[variant] },
    { Icon: Building2, label: "Inside a medical centre", sub: "Discreet by design" },
  ];
  return (
    <ul className={`flex flex-wrap gap-x-8 gap-y-3 ${className}`} data-testid="proof-strip">
      {items.map(({ Icon, label, sub }, i) => (
        <li key={label} className="flex items-center gap-2.5" data-testid={`proof-item-${i}`}>
          <Icon className="w-4 h-4 text-hairline shrink-0" strokeWidth={1.5} aria-hidden="true" />
          <span className="text-[11px] tracking-[0.2em] uppercase text-cream/85">
            {label}{sub && <span className="text-cream/55"> · {sub}</span>}
          </span>
        </li>
      ))}
    </ul>
  );
};

export const CompactPractitioner = ({ member, className = "", photo = true }) => {
  if (!member) return null;
  return (
    <Link to={`/team/${member.slug}`} className={`flex items-center gap-5 group ${className}`} data-testid="hero-practitioner">
      {photo && <div className="w-24 h-24 shrink-0 overflow-hidden rounded-[2px] ring-1 ring-gold/50 bg-cream">
        {member.photo ? (
          <img
            src={member.photo}
            alt={member.name}
            loading="eager"
            decoding="async"
            className="w-full h-full object-cover"
            style={{ objectPosition: "50% 18%" }}
            data-testid="hero-practitioner-portrait"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-center px-2" data-testid="portrait-placeholder">
            <span className="font-serif text-deep-sea text-sm leading-tight">{member.name}</span>
          </div>
        )}
      </div>}
      <div className={`min-w-0 ${photo ? "" : "border-l border-gold/60 pl-5"}`}>
        <p className="eyebrow eyebrow-dark">{member.title}</p>
        <p className="font-serif text-warm-white text-2xl leading-tight mt-1 group-hover:text-hairline transition-colors duration-300">{member.name}</p>
        {member.credential && <p className="text-cream/80 text-[13px] mt-1 leading-snug">{member.credential}</p>}
        {member.ahpra && <p className="text-cream/55 text-[11px] tracking-[0.14em] uppercase mt-1">AHPRA {member.ahpra}</p>}
      </div>
    </Link>
  );
};

export const OfferLine = ({ text, className = "" }) => (
  <p className={`font-serif italic text-warm-white text-[18px] lg:text-[22px] leading-snug border-l-2 border-gold pl-4 lg:pl-5 ${className}`} data-testid="offer-line">
    {text}
  </p>
);

const BRIEF_ICONS = [Clock, Moon, Hourglass];

export const TreatmentInfoPanel = ({ inBrief = [], covers = [], suits = [] }) => (
  <div className="space-y-6" data-testid="treatment-info-panel">
    <ul className="flex flex-wrap gap-x-7 gap-y-2.5" data-testid="panel-in-brief">
      {inBrief.map(({ label, value }, i) => {
        const Icon = BRIEF_ICONS[i] || Clock;
        return (
          <li key={label} className="flex items-start gap-2.5" data-testid={`panel-brief-${i}`}>
            <Icon className="w-4 h-4 text-gold shrink-0 mt-0.5" strokeWidth={1.5} aria-hidden="true" />
            <span className="text-[11px] tracking-[0.2em] uppercase text-cream/85 leading-relaxed">
              {label}<span className="text-cream/60 normal-case tracking-normal text-[13px]"> · {value}</span>
            </span>
          </li>
        );
      })}
    </ul>
    {covers.length > 0 && (
      <div data-testid="panel-covers">
        <p className="eyebrow eyebrow-dark mb-3">What this covers</p>
        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-1.5">
          {covers.slice(0, 6).map((item) => {
            const inner = (
              <>
                <span className="w-1.5 h-px bg-gold shrink-0 mt-3" aria-hidden="true" />
                <span className="text-cream/90 text-[15px] leading-relaxed hover:text-hairline transition-colors duration-300">{item.label}</span>
              </>
            );
            const key = item.id || item.to;
            const testId = `panel-cover-${key.replace(/[^a-z0-9]+/gi, "-").replace(/^-|-$/g, "")}`;
            return (
              <li key={key}>
                {item.to ? (
                  <Link to={item.to} className="flex items-start gap-3" data-testid={testId}>{inner}</Link>
                ) : (
                  <a href={`#covers-${item.id}`} onClick={(e) => { e.preventDefault(); scrollToId(`covers-${item.id}`, -110); }} className="flex items-start gap-3" data-testid={testId}>{inner}</a>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    )}
    {suits.length > 0 && (
      <div data-testid="panel-suits">
        <p className="eyebrow eyebrow-dark mb-3">Who it suits</p>
        <ul className="space-y-1.5">
          {suits.slice(0, 2).map((line) => (
            <li key={line} className="flex items-start gap-3 text-cream/85 text-[15px] leading-relaxed">
              <span className="w-1.5 h-1.5 rounded-full bg-gold shrink-0 mt-2.5" aria-hidden="true" />
              {line}
            </li>
          ))}
        </ul>
      </div>
    )}
  </div>
);

export const InlineConcierge = ({ member, hair = false, bookTo = "/book" }) => {
  const { pathname } = useLocation();
  const [done, setDone] = useState(false);
  const { register, handleSubmit, reset, formState: { isSubmitting } } = useForm({
    defaultValues: { practitioner: member?.name || PRACTITIONERS[0], best_time: "Any time" },
  });

  const onSubmit = async (data) => {
    try {
      const lead = leadContext("consult_request", data.practitioner);
      const stage = hair ? getHairStage() : "";
      await postEnquiry("concierge", { ...data, page: pathname, ...(hair ? { conversation: "In person at Cottesloe", ...(stage ? { stage } : {}) } : {}), meta: lead });
      trackEvent("consult_request", { page: pathname, practitioner: data.practitioner }, lead);
      if (hair) trackEvent("hair_callback_requested", { page: pathname });
      setDone(true);
      reset();
      toast.success("Received — thank you.");
    } catch {
      toast.error("Something went wrong. Please try again, or call the practice.");
    }
  };

  return (
    <div id="inline-concierge" className="relative bg-sand p-6 sm:p-9" data-testid="inline-concierge">
      <div className="gold-frame inset-3 opacity-70" aria-hidden="true" />
      <div className="relative">
        {done ? (
          <div className="py-6" data-testid="inline-concierge-success">
            <h3 className="text-deep-sea">Thank you.</h3>
            <p className="text-ink/80 mt-3 leading-relaxed">We'll call you {RESPONSE_PROMISE} to arrange your consultation.</p>
          </div>
        ) : (
          <>
            <h3 className="text-deep-sea" data-testid="inline-concierge-heading">{hair ? "Prefer to talk first?" : "Request a consultation"}</h3>
            <p className="text-ink/70 text-sm mt-2" data-testid="inline-concierge-subline">{hair ? `Request a callback — we'll call you ${RESPONSE_PROMISE}.` : `We'll call you ${RESPONSE_PROMISE}.`}</p>
            <form onSubmit={handleSubmit(onSubmit)} className="mt-5 space-y-4" data-testid="inline-concierge-form">
              <Honeypot register={register} />
              {hair && <HairStageChip />}
              <div>
                <label htmlFor="ic-name" className="eyebrow block mb-2">Name</label>
                <input id="ic-name" required autoComplete="name" data-testid="inline-name-input" className={inputClass} {...register("name", { required: true })} />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="ic-phone" className="eyebrow block mb-2">Mobile</label>
                  <input id="ic-phone" type="tel" required autoComplete="tel" data-testid="inline-phone-input" className={inputClass} {...register("phone", { required: true })} />
                </div>
                <div>
                  <label htmlFor="ic-email" className="eyebrow block mb-2">Email</label>
                  <input id="ic-email" type="email" required autoComplete="email" data-testid="inline-email-input" className={inputClass} {...register("email", { required: true })} />
                </div>
              </div>
              <div>
                <label htmlFor="ic-practitioner" className="eyebrow block mb-2">Preferred practitioner</label>
                <select id="ic-practitioner" data-testid="inline-practitioner-input" className={inputClass} {...register("practitioner")}>
                  {PRACTITIONERS.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div>
                <label htmlFor="ic-best-time" className="eyebrow block mb-2">Best time to call</label>
                <select id="ic-best-time" data-testid="inline-best-time-input" className={inputClass} {...register("best_time")}>
                  {["Any time", "Morning", "Afternoon"].map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <label className="flex items-start gap-3 text-xs text-ink/75 leading-relaxed cursor-pointer pt-1">
                <input type="checkbox" required data-testid="inline-consent-checkbox" className="mt-0.5 w-4 h-4 accent-[#233A35]" {...register("consent", { required: true })} />
                I consent to Lumière Aesthetics contacting me about my request, as described in the privacy policy.
              </label>
              <button type="submit" disabled={isSubmitting} data-testid="inline-submit-button" className="btn-gold w-full py-4 text-sm font-medium tracking-[0.06em] disabled:opacity-60">
                {isSubmitting ? "Sending…" : hair ? "Request a callback" : "Request a consultation"}
              </button>
            </form>
            <p className="text-soft-grey text-xs mt-5 leading-relaxed" data-testid="inline-concierge-footer">
              Or call <a href={`tel:${CLINIC.phoneTel}`} className="hover:text-gold transition-colors" data-testid="inline-phone-link">{CLINIC.phone}</a>
              <span className="mx-2">·</span>
              <Link to={bookTo} className="hover:text-gold transition-colors" data-testid="inline-book-online-link">Book online →</Link>
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export const ConversionHero = ({ eyebrow, title, standfirst, member, variant = "procedure", offer, image, hair = false, panel = null, testId = "conversion-hero" }) => (
  <section className="relative bg-deep-sea lg:min-h-screen lg:grid lg:grid-cols-5" data-testid={testId}>
    {image && (
      <div className="hidden lg:block absolute inset-y-0 left-0 w-3/5" aria-hidden="true">
        <img
          src={image.id}
          alt=""
          loading="eager"
          decoding="async"
          style={{ objectPosition: image.pos || "70% 25%" }}
          className="filmic absolute inset-0 w-full h-full object-cover"
          data-testid="hero-image"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[rgba(35,58,53,0.97)] via-[rgba(35,58,53,0.72)] to-[rgba(35,58,53,0.18)]" />
        <div className="absolute inset-0 bg-gradient-to-t from-[rgba(35,58,53,0.9)] via-transparent to-[rgba(35,58,53,0.4)]" />
      </div>
    )}
    {image && (
      <div className="lg:hidden relative mt-16 aspect-[4/3] sm:aspect-[16/9] overflow-hidden" data-testid="hero-image-mobile">
        <img
          src={image.id}
          alt={image.alt || ""}
          loading="eager"
          decoding="async"
          style={{ objectPosition: image.mobilePos || image.pos || "50% 30%" }}
          className="filmic absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-[rgba(35,58,53,0.85)] to-transparent" aria-hidden="true" />
      </div>
    )}

    <div className={`relative z-10 lg:col-span-3 px-6 sm:px-10 lg:pl-[120px] lg:pr-16 ${image ? "pt-8" : "pt-[88px]"} pb-6 lg:py-36 flex flex-col justify-center`}>
      <Reveal y={16}>
        <p className="eyebrow eyebrow-dark">{eyebrow}</p>
        <h1 className="text-warm-white max-w-2xl" data-testid="hero-heading">{title}</h1>
        {standfirst && <p className="standfirst text-cream/90 max-w-xl text-[19px] lg:text-[24px] !mt-5 lg:!mt-8" data-testid="hero-standfirst">{standfirst}</p>}
      </Reveal>
      <Reveal y={12} delay={0.1}>
        <ProofStrip variant={variant} className="hidden lg:flex mt-10" />
        {panel ? <div className="mt-6 lg:mt-10">{panel}</div> : <CompactPractitioner member={member} className="mt-6 lg:mt-10" />}
        <OfferLine text={offer} className="mt-5 lg:mt-6" />
      </Reveal>
    </div>

    <div className="relative z-10 lg:col-span-2 bg-cream px-5 sm:px-10 lg:px-12 py-6 lg:py-36 flex flex-col justify-center">
      <Reveal y={12} delay={0.15}>
        <InlineConcierge member={member} hair={hair} />
      </Reveal>
    </div>

    <div className="lg:hidden relative z-10 px-6 sm:px-10 py-6 border-t border-cream/10">
      <ProofStrip variant={variant} />
    </div>
  </section>
);

export const CredentialsStrip = ({ ids = [] }) => {
  const members = ids.map(getTeamMember).filter(Boolean);
  if (!members.length) return null;
  return (
    <section className="bg-cream border-b border-hairline/40" data-testid="credentials-strip">
      <div className="shell py-5 space-y-1.5">
        {members.map((m) => (
          <p key={m.id} className="text-ink/75 text-[13px] leading-relaxed" data-testid={`credential-${m.id}`}>
            <span className="text-deep-sea">{m.name}</span> — {m.proof}
            {m.ahpra && <span className="text-soft-grey"> · AHPRA {m.ahpra}</span>}
          </p>
        ))}
      </div>
    </section>
  );
};

export const StickyCTA = ({ practitioner, hair = false }) => {
  const concierge = useConcierge();
  const [scrolled, setScrolled] = useState(false);
  const [formInView, setFormInView] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > window.innerHeight * 0.3);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const el = document.getElementById("inline-concierge");
    if (!el || typeof IntersectionObserver === "undefined") return undefined;
    const io = new IntersectionObserver(([entry]) => setFormInView(entry.isIntersecting), { threshold: 0.15 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const request = () => {
    if (document.getElementById("inline-concierge")) scrollToId("inline-concierge", -96);
    else concierge.open({ practitioner, hair });
  };
  const show = scrolled && !formInView;

  return (
    <div
      className={`lg:hidden fixed bottom-0 inset-x-0 z-40 bg-deep-sea border-t border-gold/30 transition-transform duration-500 ${show ? "translate-y-0" : "translate-y-full"}`}
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      data-testid="sticky-cta"
      data-visible={show ? "true" : "false"}
      aria-hidden={!show}
    >
      <div className="grid grid-cols-[1fr_auto] gap-3 p-3">
        <button onClick={request} tabIndex={show ? 0 : -1} data-testid="sticky-cta-request" className="btn-gold w-full py-3.5 text-sm font-medium tracking-[0.06em]">
          Request a consultation
        </button>
        <a href={`tel:${CLINIC.phoneTel}`} tabIndex={show ? 0 : -1} data-testid="sticky-cta-call" className="btn-outline-gold inline-flex items-center px-6 py-3.5 text-sm font-medium tracking-[0.06em]">
          Call
        </a>
      </div>
    </div>
  );
};

export const FirstVisitBox = ({ offer }) => (
  <div className="bg-cream rounded-lg p-8" data-testid="first-visit-box">
    <h3 className="text-deep-sea">What a first visit involves</h3>
    <ul className="mt-4 space-y-2 text-ink/80 text-[15px] leading-relaxed">
      <li className="flex gap-3"><span className="w-1.5 h-1.5 rounded-full bg-gold mt-2.5 shrink-0" aria-hidden="true" />Your history and what's bothering you, in your words</li>
      <li className="flex gap-3"><span className="w-1.5 h-1.5 rounded-full bg-gold mt-2.5 shrink-0" aria-hidden="true" />An examination and an honest plan, with costs in writing</li>
      <li className="flex gap-3"><span className="w-1.5 h-1.5 rounded-full bg-gold mt-2.5 shrink-0" aria-hidden="true" />No treatment at a first visit</li>
    </ul>
    <p className="font-serif italic text-sea-green text-lg mt-5 border-l-2 border-gold pl-4" data-testid="first-visit-fee-line">{offer}</p>
    <Link to="/first-visit" className="link-sea text-sm font-medium inline-block mt-4" data-testid="first-visit-link">What to expect</Link>
  </div>
);

